import DropdownButton from "app/components/Dropdown";
import React, { useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import ButtonSimple from "app/components/Button/ButtonSimple";
import ButtonVariant from "app/components/Button/ButtonVariant";
import InputDatePicker from "app/components/DatePicker/InputDatePicker";
import { DateTime } from "luxon";
import Select from "react-select";
import { SelectStyles } from "app/routes/Calendar/EventForm/constants";
import { DatesOptions, FilterState, useGetAttendanceOptions, USGACourse, USGATee } from "./utils";

interface Props {
  isModalOpen: boolean;
  modalHandler: () => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  coursesAndTees: USGACourse[];
  tees: USGATee[];
}

const ScoresFilterModal = ({ isModalOpen, modalHandler, setFilters, coursesAndTees, tees }: Props) => {
  const { t } = useTranslation();
  const [playedDatesOptions, setPlayedDatesOptions] = useState<DatesOptions[]>(useGetAttendanceOptions());
  const [attendance, setAttendance] = useState<DatesOptions>(playedDatesOptions[0]);
  const [selectedCourse, setSelectedCourse] = useState<USGACourse>({
    courseId: null,
    courseName: 'All Courses',
    tees: []
  });
  const [selectedTee, setSelectedTee] = useState<USGATee>({
    teeSetId: null,
    teeName: 'All Tees'
  });

  const onFilterModalClose = () => {
    setAttendance(playedDatesOptions[0]);
    setSelectedCourse(coursesAndTees.find(option => option.courseId === null));
    setSelectedTee(tees.find(option => option.teeSetId === null));
    modalHandler();
  };

  const onFilterModalCloseSafe = () => {
    const calendarEl = document.getElementById('datepicker-portal');

    if (calendarEl && calendarEl.style.display !== 'none') {
      return;
    }

    onFilterModalClose();
  };

  const onApplyClick = () => {
    setFilters((prev) => ({
      ...prev,
      attendance,
      course: selectedCourse?.courseId,
      tee: selectedTee?.teeSetId,
    }));
    modalHandler();
  };

  const onResetClick = () => {
    onFilterModalClose();
    setFilters({
      attendance: playedDatesOptions[0],
      course: null,
      tee: null,
    });
    const newOptions = playedDatesOptions.map(option => {
      if (option.text === 'Custom Date') {
        return {
          ...option,
          range: {
            start: DateTime.now().startOf('month'),
            end: DateTime.now().endOf('month'),
          }
        };
      }

      return option;
    });
    setPlayedDatesOptions(newOptions);
  };

  const onChangeCustomDate = (key: string, date: DateTime) => {
    const newOptions = playedDatesOptions.map(option => {
      if (option.id === attendance.id) {
        return {
          ...option,
          range: {
            ...option.range,
            [key]: date,
          }
        };
      }

      return option;
    });

    setPlayedDatesOptions(newOptions);
    setAttendance(prev => newOptions.find(option => option.id === prev.id));
  };

  const isMobile = window.innerWidth <= 460;

  return (
    <DropdownButton
        dropdownWrapperClass={styles.dropdown_container}
        isOpen={isModalOpen}
        onClose={onFilterModalCloseSafe}
    >
      <h1 className={styles.title}>{t('Filters')}</h1>
      <div className={styles.attendance_container}>
        <h1 className={styles.filter_name}>{t('Attendance')}</h1>

        <div className={styles.options_container}>
          {playedDatesOptions.map(option => (
            <ButtonSimple
              key={option.id}
              buttonText={option.text}
              onClick={() => setAttendance(option)}
              isActive={attendance.id === option.id}
            />
          ))}
        </div>
      </div>
      {attendance.withRange && <div className={styles.custom_range_container}>
        <div className={styles.input_date_container}>
          <label>{t('From')}</label>
          <InputDatePicker
            key='start'
            placeholderText={t('Select')}
            setDate={(date) => onChangeCustomDate('start', date)}
            date={attendance.range.start}
            minDate={DateTime.now().minus({ years: 20 }).toJSDate()}
            portalId={isMobile ? "datepicker-portal" : null}
            withPortal={isMobile}
          />
        </div>
        <div className={styles.input_date_container}>
          <label>{t('To')}</label>
          <InputDatePicker
            key='end'
            placeholderText={t('Select')}
            setDate={(date) => onChangeCustomDate('end', date)}
            date={attendance.range.end}
            minDate={DateTime.now().minus({ years: 20 }).toJSDate()}
            portalId={isMobile ? "datepicker-portal" : null}
            withPortal={isMobile}
          />
        </div>
      </div>}

      <div className={styles.input_container}>
        <label>{t('Course')}</label>
        <Select
          styles={SelectStyles}
          placeholder={t('Select Course')}
          getOptionLabel={option => option.courseName}
          getOptionValue={option => option.courseId}
          options={coursesAndTees}
          isSearchable={false}
          value={coursesAndTees.find((option) => {
            return option.courseId === selectedCourse?.courseId;
          })}
          onChange={(selectedOption) => {
            setSelectedCourse(selectedOption as USGACourse);
          }}
        />
      </div>

      {selectedCourse?.courseId !== null && (
        <div className={styles.input_container}>
          <label>{t('Tee')}</label>
          <Select
            styles={SelectStyles}
            placeholder={t('Select Tee')}
            getOptionLabel={option => option.teeName}
            getOptionValue={option => option.teeSetId}
            options={tees}
            isSearchable={false}
            value={tees.find((option) => {
              return option.teeSetId === selectedTee?.teeSetId;
            })}
            onChange={(selectedOption) => {
              setSelectedTee(selectedOption as USGATee);
            }}
          />
        </div>
      )}

      <div className={styles.buttons_container}>
        <ButtonVariant
          title={t("Reset")}
          buttonType="secondary-outline"
          onClickFunc={onResetClick}
        />
        <ButtonVariant
          title={t("Apply")}
          buttonType="primary"
          onClickFunc={onApplyClick}
        />
      </div>
    </DropdownButton>
  );
};

export default ScoresFilterModal;
